import React from 'react';

/**
 * CenterMessage — full-viewport status card for terminal states.
 *
 * Used by public reviewer form for loading / invalid / expired / submitted /
 * already_done. Each tone gets a Material Symbol icon (audit #16 — color-blind
 * and scan-ability fix; previously only differed by tint).
 *
 * Replaces the inline CenterMessage at the bottom of MagicLinkReviewForm.tsx.
 */

export type CenterMessageTone = 'success' | 'error' | 'warning' | 'info';

export interface CenterMessageProps {
  title: string;
  body?: React.ReactNode;
  tone?: CenterMessageTone;
  /** Optional CTA — e.g. "Contact HR" button */
  action?: React.ReactNode;
}

const TONE_CLASS: Record<CenterMessageTone, string> = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  error: 'bg-red-50 border-red-200 text-red-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-stone-50 border-stone-200 text-stone-900',
};

const TONE_ICON: Record<CenterMessageTone, string> = {
  success: 'check_circle',
  error: 'error',
  warning: 'schedule',
  info: 'info',
};

const TONE_ICON_COLOR: Record<CenterMessageTone, string> = {
  success: 'text-emerald-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-stone-500',
};

const CenterMessage: React.FC<CenterMessageProps> = ({ title, body, tone = 'info', action }) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
    <div className={`max-w-md w-full text-center border rounded-2xl p-8 ${TONE_CLASS[tone]}`} role="status">
      <span
        className={`material-symbols-rounded ${TONE_ICON_COLOR[tone]}`}
        style={{ fontSize: 40 }}
        aria-hidden="true"
      >
        {TONE_ICON[tone]}
      </span>
      <h2 className="text-xl font-semibold tracking-tight mt-3 mb-2">{title}</h2>
      {body && <div className="text-base leading-relaxed">{body}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  </div>
);

export default CenterMessage;
